import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { unwrapResponseOrThrow } from '~/utils/unwrapResponse';
import type { PublicProgramme } from '~~/models/Programme';

export type PublicProgrammeSearchParams = {
	merchantId: MaybeRef<string | undefined>;
	programmeId: MaybeRef<number | undefined>;
	enabled?: MaybeRef<boolean>;
};

export const fetchPublicProgrammeByIdFn = async (merchantId: string, programmeId: number) => {
	const { $api } = useNuxtApp();
	return $api
		.GET(`/programmes/{id}`, {
			params: {
				path: { id: programmeId },
				query: { merchantId },
			},
		})
		.then(unwrapResponseOrThrow);
};

export const usePublicProgrammeQuery = (
	options: PublicProgrammeSearchParams,
) => {
	const queryClient = useQueryClient();

	const enabled = computed(() => !!unref(options.programmeId) && !!unref(options.merchantId) && unref(options.enabled) !== false);

	const query = useQuery({
		queryKey: ['public-programme', options.merchantId, options.programmeId],
		queryFn: () => fetchPublicProgrammeByIdFn(unref(options.merchantId)!, unref(options.programmeId)!),
		enabled,
		initialData: () => queryClient.getQueriesData<PublicProgramme[]>({ queryKey: ['public-programmes'] })
			.flatMap(([, _data]) => _data)
			.find((c) => c?.id === unref(options.programmeId) && c?.merchantId === unref(options.merchantId)),
		initialDataUpdatedAt: () =>
			queryClient.getQueryState<PublicProgramme[]>(['public-programmes'])
				?.dataUpdatedAt,
	});

	return query;
};
